import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom'

import { useAppSelector } from 'app/store'
import rootRoutes, { ROOT_ROUTE } from 'app/routes'
import routesConstants from 'app/constants/routes.constants'
import SoundPopupContainer from 'app/components/sounds/sound-popup/sound-popup-container/sound-popup-container.component'
import RobotSpinner from 'app/components/spinner/robot-spinner.component'
import NotFoundPage from 'app/pages/errors/notFound.page'
import SalesforceChatService from 'app/services/salesforce-chat/salesforce-chat.service'
import { getAmpConfig } from 'app/services/api/amp-config-service'

import { SOUNDS_TYPE } from './constants/sounds.constants'
import { getSoundsCategory } from './utils/sounds'
import Tutorial from './components/tutorial/Tutorial/Tutorial'
import { ProgramContext } from './contexts/program/program.context'
import TutorialSettingsProvider from './components/tutorial/TutorialSettingsProvider/TutorialSettingsProvider'
import PageTitle from './components/page-title/page-title.component'
import useGradeFilters from './hooks/use-grade-filters'

interface Props {
  routes: ROOT_ROUTE[]
}

export const App = ({ routes }: Props) => {
  const location = useLocation<Location>()
  const background = location.state?.background

  const soundCategory = getSoundsCategory(location.pathname)
  const categoryPath =
    soundCategory === SOUNDS_TYPE.SOUNDS
      ? routesConstants.SOUNDS_ID
      : routesConstants.SONIDOS_ID

  const appState = useAppSelector((state) => state)
  const ampConfig = getAmpConfig()

  useEffect(() => {
    if (ampConfig?.flags?.isSalesforceChatEnabled) {
      SalesforceChatService.init(ampConfig)
    }

    return () => SalesforceChatService.cleanup()
  }, [ampConfig])

  return (
    <Suspense fallback={<RobotSpinner />}>
      <PageTitle />
      <Switch location={background || location}>
        <Route
          path={routesConstants.HOME}
          render={() => (
            <Switch>
              {routes.map((route) => (
                <Route
                  key={route.path}
                  exact={route.exact}
                  path={route.path}
                  component={
                    !route.checkAccess || route.checkAccess(appState)
                      ? route.component
                      : NotFoundPage
                  }
                />
              ))}
            </Switch>
          )}
        />
        <Route component={NotFoundPage} />
      </Switch>
      <Route path={categoryPath} component={SoundPopupContainer} />
      <Tutorial />
    </Suspense>
  )
}

const AppWithRouter = () => {
  const gradeFilters = useGradeFilters()
  const [filterValue, setFilterValue] = useState<string>(gradeFilters[0])

  const contextProviderValue = useMemo(
    () => ({ filterValue, setFilterValue }),
    [filterValue]
  )

  return (
    <BrowserRouter basename={import.meta.env.VITE_APP_ROUTER_BASENAME}>
      <ProgramContext.Provider value={contextProviderValue}>
        <TutorialSettingsProvider>
          <App routes={rootRoutes} />
        </TutorialSettingsProvider>
      </ProgramContext.Provider>
    </BrowserRouter>
  )
}

export default AppWithRouter
